/* eslint-disable react/display-name */
import flatten from 'flat';
import Head from 'next/head';
import React from 'react';
import { createIntl } from 'react-intl';
import { IPageUrl } from 'src/api/graphql/page/page-url';
import { isProduction, learnHost } from 'src/env';
import { IPageSeo } from '../api/graphql';
import { I18nProvider } from '../context/i18nContext';
import { ILocaleMessage } from './lang';
import { getSeoFormatLang } from './seo';
import {
  getActualLocale,
  getActualUrl,
  getPathLocale,
  getSeoFormatUrl,
} from './url';

interface IPageProps {
  locale: string;
  pageEntry: {
    page_seo?: IPageSeo;
    [x: string]: any;
  };
  currentUrl?: string;
  [x: string]: any;
}

const withPageSeo = (Component: React.FC<IPageProps>) => (props) => {
  const { pageEntry, locale, currentUrl, pageUrls } = props;

  let robots = (
    <meta
      name="robots"
      content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    />
  );

  if (!isProduction) {
    robots = <meta name="robots" content="noindex" />;
  } else if (pageEntry?.page_seo?.robots?.length > 0) {
    robots = (
      <meta name="robots" content={pageEntry?.page_seo?.robots?.join(',')} />
    );
  }

  if (process.browser) {
    localStorage.setItem('LANG_KEY', locale);
  }

  return (
    <>
      <Head>
        <title>{pageEntry?.page_seo?.title || pageEntry?.title}</title>

        {/* Canonical */}
        {pageEntry?.page_seo?.canonical_url?.length > 0 ? (
          <link
            rel="canonical"
            href={getSeoFormatUrl(
              getActualUrl(pageEntry?.page_seo?.canonical_url)
            )}
          />
        ) : (
          <link
            rel="canonical"
            href={getSeoFormatUrl(
              `${learnHost}${getPathLocale(locale)}${currentUrl}`
            )}
          />
        )}

        <meta
          property="og:title"
          content={pageEntry?.page_seo?.title || pageEntry?.title}
        />

        <link
          rel="og:url"
          href={getSeoFormatUrl(
            `${learnHost}/${getPathLocale(locale)}${currentUrl}`
          )}
        />

        {/* hrefLang */}
        {pageUrls?.map(({ locale, key: path }) => {
          if (locale === 'en') {
            return (
              <>
                <link
                  rel="alternate"
                  hrefLang="x-default"
                  href={getSeoFormatUrl(
                    `${learnHost}${getPathLocale(locale)}/${path}`
                  )}
                />
                <link
                  rel="alternate"
                  hrefLang={getSeoFormatLang(locale)}
                  href={getSeoFormatUrl(
                    `${learnHost}${getPathLocale(locale)}/${path}`
                  )}
                  key={locale}
                />
              </>
            );
          }

          return (
            <link
              rel="alternate"
              hrefLang={getSeoFormatLang(getActualLocale(locale))}
              href={getSeoFormatUrl(
                `${learnHost}${getPathLocale(getActualLocale(locale))}/${path}`
              )}
              key={locale}
            />
          );
        })}

        <meta
          property="og:description"
          content={pageEntry?.page_seo?.description}
        />
        <meta property="og:type" content={'website'} />
        <meta property="type" content={'website'} />
        {pageEntry?.page_seo?.imageConnection?.edges?.length > 0 && (
          <meta
            property="og:image"
            content={pageEntry?.page_seo?.imageConnection?.edges[0].node.url}
          />
        )}
        <meta property="og:locale" content={getSeoFormatLang(locale)} />
        <meta property="locale" content={getSeoFormatLang(locale)} />
        <meta name="description" content={pageEntry?.page_seo?.description} />

        {/* Robots */}
        {robots}

        {/* Keywords */}
        <meta
          name="keywords"
          content={pageEntry?.page_seo?.keywords?.join(',')}
        />
        <meta name="twitter:card" content="summary_large_image" />
        {pageEntry?.page_seo?.imageConnection?.edges?.length > 0 && (
          <meta
            property="twitter:image"
            content={pageEntry?.page_seo?.imageConnection?.edges[0].node.url}
          />
        )}
        <meta name="twitter:creator" content="@zoomexofficial" />
        <meta name="twitter:site" content="@zoomexofficial" />
        <meta name="twitter:label1" content="Written by" />
        <meta name="twitter:data1" content="Zoomex Media" />
        <meta property="twitter:domain" content={learnHost} />
        <meta
          property="twitter:url"
          content={getSeoFormatUrl(
            `${learnHost}${getPathLocale(locale)}${currentUrl}`
          )}
        />
        <meta
          name="twitter:title"
          content={pageEntry?.page_seo?.title || pageEntry?.title}
        />
        <meta
          name="twitter:description"
          content={pageEntry?.page_seo?.description}
        />
      </Head>
      <Component {...props} />
    </>
  );
};

interface Ii18n {
  locale: string;
  localeMessages: ILocaleMessage;
  [x: string]: any;
}

const withI18n = (Component: React.FC<Ii18n>) => (props: any) => {
  const { locale, localeMessages = {} } = props;
  const intl = createIntl({ messages: flatten(localeMessages), locale });

  return (
    <I18nProvider intl={intl} locale={locale}>
      <Component {...props} />
    </I18nProvider>
  );
};

interface IHoc {
  locale: string;
  localeMessages: ILocaleMessage;
  pageEntry: {
    page_seo?: IPageSeo;
    [x: string]: any;
  };
  currentUrl?: string;
  pageUrls?: Array<IPageUrl>;
  [x: string]: any;
}

const withHoc = (Component: React.FC<IHoc>) => withI18n(withPageSeo(Component));
export { withHoc, withI18n, withPageSeo };
